/**
 * React Query hooks for the TeamLeader Dashboard.
 * All data is scoped to the current TeamLeader's groups via the backend.
 */
import { useQuery } from "@tanstack/react-query";
import { useAuth } from "@/hooks/use-auth";
import {
  fetchTLGroupsWithStudents,
  fetchTLUnreadCount,
  type TLGroupWithStudents,
} from "@/lib/api/tl-dashboard";
import { fetchGradingQueue } from "@/lib/api/exams";
import { fetchStudentStatusCounts, type StudentStatusCounts } from "@/lib/api/students";
import { keys } from "@/hooks/use-tl-exams";
import type { GradingQueueItem } from "@/lib/types";

import { OPERATIONAL_QUERY_OPTIONS, TL_QUERY_KEYS } from "@/lib/api/query-options";

export const tlKeys = TL_QUERY_KEYS;

// ── Hooks ────────────────────────────────────────────────────────────────────

/** Fetches all groups with their students, enriched with counts. */
export function useTLGroupsWithStudents() {
  const { user } = useAuth();
  return useQuery({
    queryKey: tlKeys.dashboardGroups,
    queryFn: fetchTLGroupsWithStudents,
    enabled: !!user?.id,
    ...OPERATIONAL_QUERY_OPTIONS,
  });
}

/** Derives dashboard stats from the groups+students data. */
export function useTLDashboardStats() {
  const { data: groups, isLoading, error } = useTLGroupsWithStudents();

  const stats = groups
    ? deriveStats(groups)
    : {
        totalStudents: 0,
        totalGroups: 0,
        activeStudents: 0,
        atRiskStudents: [],
        atRiskCount: 0,
      };

  return { stats, groups: groups ?? [], isLoading, error };
}

/** Unread message count, refreshed every 30s. */
export function useTLUnreadCount() {
  const { user } = useAuth();
  return useQuery({
    queryKey: tlKeys.unreadCount(user?.id ?? ""),
    queryFn: () => fetchTLUnreadCount(user!.id),
    enabled: !!user?.id,
    staleTime: 10_000,
    refetchInterval: 30_000,
  });
}

/** Cross-exam pending grading queue. */
export function useTLGradingQueue() {
  const { user } = useAuth();
  return useQuery<GradingQueueItem[]>({
    queryKey: keys.gradingQueueAll,
    queryFn: () => fetchGradingQueue(),
    enabled: !!user?.id,
    ...OPERATIONAL_QUERY_OPTIONS,
  });
}

/** Student status counts for dashboard training status breakdown. */
export function useTLStatusCounts() {
  const { user } = useAuth();
  return useQuery<StudentStatusCounts>({
    queryKey: tlKeys.statusCounts,
    queryFn: fetchStudentStatusCounts,
    enabled: !!user?.id,
    ...OPERATIONAL_QUERY_OPTIONS,
  });
}

// ── Helpers ──────────────────────────────────────────────────────────────────

function deriveStats(groups: TLGroupWithStudents[]) {
  const allStudents = groups.flatMap((g) => g.students);
  // Deduplicate by student id (a student could be in multiple groups)
  const unique = new Map(allStudents.map((s) => [s.id, s]));
  const students = Array.from(unique.values());

  const totalStudents = students.length;
  const totalGroups = groups.length;
  const activeStudents = students.filter(
    (s) => s.status === "approved" || s.status === "active" || s.status === "Active",
  ).length;
  const atRiskStudents = students.filter(
    (s) =>
      s.status === "at_risk" ||
      s.status === "AtRisk" ||
      s.status === "at-risk" ||
      s.status === "flagged",
  );

  return {
    totalStudents,
    totalGroups,
    activeStudents,
    atRiskStudents,
    atRiskCount: atRiskStudents.length,
  };
}
